import * as React from 'react';

import { enumFacilityGroupSortOption } from '@rematter/frontend-lib-api-client';
import { FacilityGroupRecord } from '@rematter/modules/facilities/types/facilityGroup';
import {
  Badge,
  Button,
  DataGrid,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
  SearchField,
  Separator,
  type PaginationProps,
  useDataGridSelection,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

import { FacilityGroupDialog } from './FacilityGroupDialog';
import { useFacilityGroupListColumns } from './hooks/useFacilityGroupListColumns';

import {
  $limit,
  $offset,
  $searchTerm,
} from '~/modules/facilities/_state/facilityGroupsList.state';
import { useDeleteFacilityGroupMutation } from '~/modules/facilities/hooks/mutations/useDeleteFacilityGroupMutation';
import { useFacilityGroupsQuery } from '~/modules/facilities/hooks/useFacilityGroupsQuery';
import useDebouncedValue from '~/shared/hooks/useDebounce';

//
// ⚛️ ⚛️ ⚛️ FACILITY GROUP LIST VIEW ⚛️ ⚛️ ⚛️
//
export const FacilityGroupListView = () => {
  const t = useTranslations();
  const toast = useToast();

  // States
  const [selectedRows, setSelectedRows] = React.useState<FacilityGroupRecord[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useAtom($searchTerm);
  const [offset, setOffset] = useAtom($offset);
  const [limit, setLimit] = useAtom($limit);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [editingGroup, setEditingGroup] = React.useState<FacilityGroupRecord>();

  // Other data hooks
  const debouncedSearchTerm = useDebouncedValue(searchTerm ?? '', 500);

  const {
    data: { facilityGroups: groups = [], total = 0 } = {
      facilityGroups: [],
      total: 0,
    },
    error,
    refetch,
    isLoading,
  } = useFacilityGroupsQuery({
    searchTerm: debouncedSearchTerm,
    limit,
    offset,
    sorting: [enumFacilityGroupSortOption.NameAsc],
  });

  const { mutateAsync: deleteFacilityGroup } = useDeleteFacilityGroupMutation();
  const handleSelectionChange = useDataGridSelection(groups, setSelectedRows);

  // Memoized values
  const emptyPanelProps = React.useMemo(
    () => ({
      title: 'No facility groups',
      description: <span>{t.facilityGroups.list.empty.description}</span>,
      centered: true,
    }),
    [t],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(({ limit: newLimit, offset: newOffset }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  }, []);
  const handleSearch = React.useCallback((value: string) => {
    setSearchTerm(value || undefined);
    setOffset(0);
  }, []);
  const handleEditGroup = React.useCallback((group: FacilityGroupRecord) => {
    setEditingGroup(group);
    setIsEditDialogOpen(true);
  }, []);
  const handleCreateGroup = React.useCallback(() => {
    setEditingGroup(undefined);
    setIsEditDialogOpen(true);
  }, []);
  const handleCloseEditDialog = React.useCallback(() => {
    setIsEditDialogOpen(false);
    setEditingGroup(undefined);
  }, []);
  const handleDelete = React.useCallback(async () => {
    try {
      await Promise.all(
        selectedRows.map((row) => deleteFacilityGroup({ id: row.id })),
      );
      toast.success(t.facilityGroups.dialog.deleteSuccess(selectedRows.length));
      setSelectedRows([]);
      refetch();
    } catch (err) {
      console.error(err);
      toast.error(t.facilityGroups.dialog.genericError);
    }
  }, [selectedRows, deleteFacilityGroup, refetch, toast, t]);

  const columns = useFacilityGroupListColumns({ onEdit: handleEditGroup });

  return (
    <>
      <ListScreenLayout
        className="@FacilityGroupListView"
        filters={
          <SearchField
            autoFocus
            value={searchTerm ?? ''}
            onChange={handleSearch}
            placeholder={t.facilityGroups.list.search.placeholder}
            className="w-64"
          />
        }
        actions={
          <Button
            onPress={handleCreateGroup}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.facilityGroups.list.actions.create}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<FacilityGroupRecord>
          isLoading={isLoading}
          columns={columns}
          dataSource={groups}
          rowKey="id"
          aria-label={t.facilityGroups.list.aria}
          selectionMode="multiple"
          onSelectionChange={handleSelectionChange}
          className="w-full text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.facilityGroups.errors.loadingGroups(
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <span className="text-sm">
          <Badge color="primary" size="small">
            {selectedRows.length}
          </Badge>{' '}
          {t.facilityGroups.list.selected(selectedRows.length)}
        </span>
        <Separator hideDivider />
        <Button
          variant="solid"
          color="failure"
          icon={<LucideTrash />}
          isDisabled={selectedRows.length < 1}
          onPress={handleDelete}
        >
          {t.facilityGroups.list.actions.delete(selectedRows.length)}
        </Button>
      </FloatingToolbar>

      <FacilityGroupDialog
        group={editingGroup}
        isOpen={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        onSuccess={refetch}
      />
    </>
  );
};
